import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Delete, Add, ArrowBack, Search, FilterList, GridOn } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import PescadorForm from './PescadorForm';

const PescadoresList = () => {
  const navigate = useNavigate();
  const [pescadores, setPescadores] = useState<any[]>([]);
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tempSearch, setTempSearch] = useState("");

  const [projetos, setProjetos] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [colonias, setColonias] = useState<any[]>([]);
  const [municipios, setMunicipios] = useState<any[]>([]);

  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroProjeto, setFiltroProjeto] = useState("");
  const [filtroArea, setFiltroArea] = useState("");
  const [filtroColonia, setFiltroColonia] = useState("");
  const [filtroMunicipio, setFiltroMunicipio] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [pescadorEditandoUid, setPescadorEditandoUid] = useState<string | null>(null);

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
    api.get('/pesca/areas').then(res => setAreas(res.data.data));
    api.get('/pesca/colonias').then(res => setColonias(res.data.data));
    api.get('/pesca/pescadores-municipios').then(res => setMunicipios(res.data.data));
  }, []);

  const fetchPescadores = useCallback((
    pageParam: number,
    searchTerm: string,
    filtros: { projeto: string; area: string; colonia: string; municipio: string },
    isNew: boolean
  ) => {
    const params = new URLSearchParams({ page: String(pageParam), search: searchTerm, limit: '20' });
    if (filtros.projeto) params.set('uid_pesca_projeto', filtros.projeto);
    if (filtros.area) params.set('uid_pesca_area', filtros.area);
    if (filtros.colonia) params.set('uid_pesca_colonia', filtros.colonia);
    if (filtros.municipio) params.set('id_municipio', filtros.municipio);

    api.get(`/pesca/pescadores?${params.toString()}`)
      .then(res => {
        const novos = res.data.data;
        const totalGeral = res.data.total;

        setPescadores(prev => {
          const lista = isNew ? novos : [...prev, ...novos];
          setTemMais(lista.length < totalGeral);
          return lista;
        });
        setTotal(totalGeral);
      });
  }, []);

  const filtrosAtuais = { projeto: filtroProjeto, area: filtroArea, colonia: filtroColonia, municipio: filtroMunicipio };

  // Debounce para pesquisa
  useEffect(() => {
    const handler = setTimeout(() => setSearch(tempSearch), 500);
    return () => clearTimeout(handler);
  }, [tempSearch]);

  // Trigger de busca / filtro
  useEffect(() => {
    setPagina(1);
    fetchPescadores(1, search, filtrosAtuais, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filtroProjeto, filtroArea, filtroColonia, filtroMunicipio, fetchPescadores]);

  // Carregar mais
  useEffect(() => {
    if (pagina > 1) fetchPescadores(pagina, search, filtrosAtuais, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, fetchPescadores]);

  const limparFiltros = () => {
    setFiltroProjeto("");
    setFiltroArea("");
    setFiltroColonia("");
    setFiltroMunicipio("");
  };

  const qtdFiltrosAtivos = [filtroProjeto, filtroArea, filtroColonia, filtroMunicipio].filter(Boolean).length;

  const handleNovo = () => {
    setPescadorEditandoUid(null);
    setFormOpen(true);
  };

  const handleEditar = (uid: string) => {
    setPescadorEditandoUid(uid);
    setFormOpen(true);
  };

  const handleExcluir = async (p: any) => {
    if (!window.confirm(`Remover o pescador "${p.nome}"?`)) return;
    await api.delete(`/pesca/pescadores/${p.uid_pesca_pescador}`);
    fetchPescadores(1, search, filtrosAtuais, true);
    setPagina(1);
  };

  const handleSalvo = () => {
    setFormOpen(false);
    fetchPescadores(1, search, filtrosAtuais, true);
    setPagina(1);
  };

  return (
  <div className={styles.container}>
    <div className={styles.headerTitle}>
      <h1 className={styles.greenTitle}>Pescadores</h1>

      <div style={{ position: 'relative', flex: 1, maxWidth: '400px', margin: '0 20px' }}>
        <Search style={{ position: 'absolute', left: '10px', top: '10px', color: '#888' }} />
        <input
          type="text"
          placeholder="Pesquisar pescador..."
          value={tempSearch}
          onChange={(e) => setTempSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 10px 10px 40px',
            borderRadius: '20px',
            border: '1px solid #ddd',
            outline: 'none'
          }}
        />
      </div>

      <div className={styles.actionsHeader}>
        <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
          Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{total}</span>
        </p>

        <button
          className={styles.blueBtn}
          onClick={() => setFiltrosAbertos(f => !f)}
          title="Filtros"
          style={{ position: 'relative' }}
        >
          <FilterList />
          {qtdFiltrosAtivos > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4, background: '#f75a68', color: '#fff',
              borderRadius: '50%', width: 16, height: 16, fontSize: '0.65rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {qtdFiltrosAtivos}
            </span>
          )}
        </button>

        <button className={styles.blueBtn} onClick={() => navigate('/admin/pescadores/matriz')} title="Matriz de pescadores">
          <GridOn />
        </button>

        <button className={styles.blueBtn} onClick={handleNovo} title="Adicionar Novo">
          <Add />
        </button>
        <button className={styles.blueBtn} onClick={() => navigate(-1)} title="Voltar">
          <ArrowBack />
        </button>
      </div>
    </div>

    {filtrosAbertos && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        <select
          value={filtroProjeto}
          onChange={(e) => setFiltroProjeto(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todos os projetos</option>
          {projetos.map((p) => (
            <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
          ))}
        </select>

        <select
          value={filtroArea}
          onChange={(e) => setFiltroArea(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todas as áreas</option>
          {areas.map((a) => (
            <option key={a.uid_pesca_area} value={a.uid_pesca_area}>{a.nome}</option>
          ))}
        </select>

        <select
          value={filtroColonia}
          onChange={(e) => setFiltroColonia(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todas as colônias</option>
          {colonias.map((c) => (
            <option key={c.uid_pesca_colonia} value={c.uid_pesca_colonia}>{c.nome}</option>
          ))}
        </select>

        <select
          value={filtroMunicipio}
          onChange={(e) => setFiltroMunicipio(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todos os municípios</option>
          {municipios.map((m) => (
            <option key={m.id_municipio} value={m.id_municipio}>{m.municipio}{m.id_estado ? ` - ${m.id_estado}` : ''}</option>
          ))}
        </select>

        {qtdFiltrosAtivos > 0 && (
          <button
            onClick={limparFiltros}
            style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}
          >
            Limpar filtros
          </button>
        )}
      </div>
    )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Município</th>
              <th>Colônia</th>
              <th>Projeto(s)</th>
              <th>Área(s) de Pesca</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {pescadores.map((p) => (
              <tr key={p.uid_pesca_pescador} onClick={() => navigate(`view/${p.uid_pesca_pescador}`)} style={{ cursor: 'pointer' }}>
                <td>{p.nome}</td>
                <td>{p.municipio?.municipio || 'Não informado'}</td>
                <td>{p.colonia?.nome || 'Não informada'}</td>
                <td>
                  {p.projetoVinculos && p.projetoVinculos.length > 0
                    ? p.projetoVinculos.map((v: any) => v.projeto?.nome).filter(Boolean).join(', ')
                    : <span style={{ color: '#999' }}>Nenhum</span>}
                </td>
                <td>
                  {p.areaVinculos && p.areaVinculos.length > 0
                    ? p.areaVinculos.map((v: any) => v.area?.nome).filter(Boolean).join(', ')
                    : <span style={{ color: '#999' }}>Nenhuma</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Edit fontSize="small" style={{ color: '#03c9fe', cursor: 'pointer' }} onClick={() => handleEditar(p.uid_pesca_pescador)} />
                    <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} onClick={() => handleExcluir(p)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
        {temMais ? (
          <button className={styles.blueBtn} style={{ width: 'auto', padding: '0 20px' }} onClick={() => setPagina(p => p + 1)}>
            Carregar Mais pescadores
          </button>
        ) : (
          <p style={{ color: '#999', fontSize: '0.9rem' }}>Todos os {total} pescadores carregados</p>
        )}
      </div>

      {formOpen && (
        <PescadorForm
          pescadorUid={pescadorEditandoUid}
          onClose={() => setFormOpen(false)}
          onSaved={handleSalvo}
        />
      )}
    </div>
  );
};

const filterSelectStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff',
};

export default PescadoresList;
